import { useState } from 'react'
import { RadarChart, Radar, PolarGrid, PolarAngleAxis, ResponsiveContainer, Tooltip } from 'recharts'
import { CheckCircle, Clock, Warning, Heartbeat, Lightning, Bicycle, Drop, Package, ArrowCounterClockwise, Tree, CloudFog, ClipboardText, MinusCircle } from '@phosphor-icons/react'

const C = {
  surface: '#FAF7F3', surfaceAlt: '#EDE7DE', border: '#DDD5C8',
  text: '#2C2017', muted: '#7A6A5A', disabled: '#B0A89E',
  brand: '#3B2F2F',
  coral: '#D4614A', slate: '#7A8FA0', olive: '#8D9A2E', amber: '#C98A2E', charcoal: '#4A4A4A',
}
const CARD = { background: 'white', borderRadius: '8px', padding: '24px', boxShadow: '0 1px 4px rgba(59,47,47,0.07)', border: `1px solid ${C.border}` }
const MONO = { fontFamily: "'IBM Plex Mono', monospace" }
const FONT = { fontFamily: "'DM Sans', sans-serif" }

// Ponderaciones medioambientales — Manual BREEAM ES NC 2015, Tabla 5 (p.18)
const WEIGHTS = {
  'Gestión': 0.115, 'Salud y Bienestar': 0.14, 'Energía': 0.18,
  'Transporte': 0.08, 'Agua': 0.105, 'Materiales': 0.12,
  'Residuos': 0.07, 'Uso del Suelo y Ecología': 0.095, 'Contaminación': 0.095,
}

const CAT_ICONS = {
  'Gestión': ClipboardText, 'Salud y Bienestar': Heartbeat, 'Energía': Lightning,
  'Transporte': Bicycle, 'Agua': Drop, 'Materiales': Package,
  'Residuos': ArrowCounterClockwise, 'Uso del Suelo y Ecología': Tree, 'Contaminación': CloudFog,
}

const PHASES = [
  { id: 1, label: 'Pre-Evaluación', color: C.coral },
  { id: 2, label: 'Diseño', color: C.slate },
  { id: 3, label: 'Ejecución', color: C.olive },
  { id: 4, label: 'Post-Construcción', color: C.amber },
  { id: 5, label: 'Certificación Final', color: C.charcoal },
]

// Umbrales BREEAM ES NC 2015 — Manual oficial (sec. Información Complementaria)
// índice = nivel alcanzable: 0=SinClasificar, 1=Correcto, 2=Bueno, 3=MuyBueno, 4=Excelente, 5=Excepcional
const RATING_THRESHOLDS = [
  { idx: 5, pct: 85, label: 'Excepcional', color: C.olive },
  { idx: 4, pct: 70, label: 'Excelente',   color: C.olive },
  { idx: 3, pct: 55, label: 'Muy Bueno',   color: C.amber },
  { idx: 2, pct: 45, label: 'Bueno',       color: C.amber },
  { idx: 1, pct: 30, label: 'Correcto',    color: C.charcoal },
]
const NO_RATING = { idx: 0, label: 'Sin Calificación', color: C.disabled }

// Devuelve el nivel máximo permitido por los puntos obtenidos en los criterios
// prerrequisito (de PREREQ_MINIMUMS). Por cada criterio, encontramos el mayor
// índice de la escala C/B/MB/E/EX cuyos puntos mínimos cumple. El nivel final
// del proyecto está acotado por el mínimo de esos índices.
function computePrereqCap(pointsByCode, prereqMinimums) {
  const blockers = []
  let cap = 5 // por defecto permite Excepcional
  Object.entries(prereqMinimums || {}).forEach(([code, mins]) => {
    const pts = pointsByCode[code] || 0
    // mins = [C, B, MB, E, EX] (puntos mínimos por nivel)
    // El nivel máximo alcanzable con `pts` es el índice más alto donde pts >= mins[i-1]
    // (idx 5=EX usa mins[4], idx 4=E usa mins[3], ... idx 1=C usa mins[0])
    let critCap = 5
    for (let levelIdx = 5; levelIdx >= 1; levelIdx--) {
      const required = mins[levelIdx - 1] || 0
      if (pts < required) critCap = levelIdx - 1
    }
    if (critCap < cap) {
      cap = critCap
      blockers.length = 0 // empezamos lista nueva con los nuevos bloqueantes más limitantes
    }
    if (critCap === cap && pts < (mins[Math.min(cap, 4)] || 0)) {
      blockers.push({ code, pts, required: mins[Math.min(cap, 4)] || 0, nextLevel: RATING_LEVELS_LOCAL[cap] })
    }
  })
  return { cap, blockers }
}

const RATING_LEVELS_LOCAL = ['Sin Calificación', 'Correcto', 'Bueno', 'Muy Bueno', 'Excelente', 'Excepcional']

// Clasificación final: el menor de los niveles permitidos por (a) % global
// y (b) prerrequisitos. Devuelve label/color/cap + lista de bloqueantes.
function getClassification(pct, pointsByCode, prereqMinimums) {
  const byPct = RATING_THRESHOLDS.find(r => pct >= r.pct) || NO_RATING
  if (!prereqMinimums || Object.keys(prereqMinimums).length === 0) {
    return { ...byPct, capped: false, blockers: [] }
  }
  const { cap, blockers } = computePrereqCap(pointsByCode, prereqMinimums)
  if (byPct.idx === 0 || byPct.idx <= cap) {
    return { ...byPct, capped: false, blockers: [] }
  }
  // Capamos al nivel permitido por prereqs
  const capped = RATING_THRESHOLDS.find(r => r.idx === cap) || NO_RATING
  return { ...capped, capped: true, byPctLabel: byPct.label, blockers }
}

export default function ExpedienteDashboard({ project, BREEAM_CRITERIA, PREREQ_MINIMUMS, currentPhase = 2, onPhaseChange, CRITERIA_PHASES = {}, PHASE_COLORS = [] }) {
  const [expandedCat, setExpandedCat] = useState(null)

  if (!project) return null

  const evals = project.evaluations || {}
  const totalEvals = Object.values(evals)

  const categoryStats = Object.entries(BREEAM_CRITERIA).map(([cat, data]) => {
    const validCodes = new Set(Object.keys(data.criteria))
    const catEvals = totalEvals.filter(e => e.category === cat && validCodes.has(e.criteria_code))
    const totalCrit = validCodes.size
    const evalByCode = new Map()
    catEvals.forEach(e => { if (!evalByCode.has(e.criteria_code)) evalByCode.set(e.criteria_code, e) })
    const uniqueEvals = Array.from(evalByCode.values())
    const naCodes = new Set()
    let cumple = 0, noAplica = 0, noCumple = 0
    uniqueEvals.forEach(e => {
      let j = null; try { j = e.justificacion ? JSON.parse(e.justificacion) : null } catch {}
      const finalPts = e.puntos_auditoria ?? e.points_obtained ?? 0
      if (j?.no_aplica) { noAplica++; naCodes.add(e.criteria_code) }
      else if (finalPts > 0) cumple++
      else noCumple++
    })
    const pendiente = Math.max(0, totalCrit - cumple - noAplica - noCumple)
    const evaluated = cumple + noAplica + noCumple
    const points = uniqueEvals.reduce((s, e) => s + (e.puntos_auditoria ?? e.points_obtained ?? 0), 0)
    // maxPts dinámico — prefiere points_max de BD (refleja max efectivo, p.ej.
    // SyB 2 sin laboratorios = 3 en vez de 5 teóricos). Fallback al teórico.
    const maxPts = Object.entries(data.criteria).reduce((s, [code, c]) => {
      if (naCodes.has(code)) return s
      const ev = evalByCode.get(code)
      const effMax = (ev && Number.isFinite(ev.points_max) && ev.points_max > 0) ? ev.points_max : (c.points || 0)
      return s + effMax
    }, 0)
    const pctExact = maxPts > 0 ? (points / maxPts) * 100 : 0
    const pct = Math.round(pctExact)
    // INO: cada punto vale +1% directo (manual p.19); resto: peso de Tabla 5
    const isInno = cat === 'Innovación'
    const weight = isInno ? null : (WEIGHTS[cat] || 0)
    const weightedScore = isInno ? points : (pctExact / 100) * weight * 100
    return { cat, data, totalCrit, evaluated, cumple, noAplica, noCumple, pendiente, points, maxPts, pct, weight, weightedScore }
  })

  const totalCriteria = categoryStats.reduce((s, c) => s + c.totalCrit, 0)
  const cumpleCount = categoryStats.reduce((s, c) => s + c.cumple, 0)
  const noAplicaCount = categoryStats.reduce((s, c) => s + c.noAplica, 0)
  const noCumpleCount = categoryStats.reduce((s, c) => s + c.noCumple, 0)
  const pendienteCount = categoryStats.reduce((s, c) => s + c.pendiente, 0)
  const evaluatedCriteria = cumpleCount + noAplicaCount + noCumpleCount
  const totalPoints = project.totalPoints || 0
  const globalPct = totalCriteria > 0 ? Math.round((evaluatedCriteria / totalCriteria) * 100) : 0
  const pctOf = (n) => totalCriteria > 0 ? (n / totalCriteria) * 100 : 0
  const weightedTotal = categoryStats.reduce((s, c) => s + c.weightedScore, 0)
  // ATENCIÓN: NO redondeamos antes de comparar con los umbrales. Redondear
  // 54,8 → 55 subiría falsamente el proyecto de «Bueno» a «Muy Bueno». La
  // clasificación oficial BREEAM ES aplica los umbrales sobre el valor
  // exacto (Tabla 3 del manual NC 2015: > 55 % para Muy Bueno, no ≥ 55 %).
  const scorePct = weightedTotal

  // Mapa { código → puntos obtenidos } — base para el cap de prerrequisitos
  const pointsByCode = {}
  totalEvals.forEach(e => {
    if (!e.criteria_code) return
    if (pointsByCode[e.criteria_code] == null) pointsByCode[e.criteria_code] = e.puntos_auditoria ?? e.points_obtained ?? 0
  })
  const classification = getClassification(scorePct, pointsByCode, PREREQ_MINIMUMS)

  const radarData = categoryStats.map(c => ({
    subject: c.cat.split(' ')[0],
    fullName: c.cat,
    actual: c.pct,
    objetivo: 65,
  }))

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', ...FONT }}>

      {/* Header: Puntuación ponderada + Barra de progreso */}
      <div style={{ ...CARD, padding: '20px 24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '14px', gap: '1.5rem', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.67rem', fontWeight: '600', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '6px' }}>Puntuación ponderada</div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '10px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '1.875rem', fontWeight: '700', color: C.text, lineHeight: 1, ...MONO }}>{weightedTotal.toFixed(1)}%</span>
              <span style={{ background: classification.color + '18', color: classification.color, padding: '2px 8px', borderRadius: '3px', fontSize: '0.7rem', fontWeight: '600' }}>{classification.label}</span>
              <span style={{ fontSize: '0.72rem', color: C.muted }}>· {totalPoints} pts brutos</span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.825rem', color: C.muted, marginBottom: '6px' }}>
              <span style={{ ...MONO, color: C.text, fontWeight: '700' }}>{evaluatedCriteria}</span>
              {' de '}
              <span style={{ ...MONO, color: C.text }}>{totalCriteria}</span>
              {' criterios evaluados · '}
              <span style={{ ...MONO, color: C.text, fontWeight: '700' }}>{globalPct}%</span>
            </div>
            <div style={{ display: 'flex', gap: '0.875rem', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
              {[['Cumple', C.olive], ['No aplica', C.slate], ['No cumple', C.coral], ['Pendiente', C.disabled]].map(([l, c]) => (
                <span key={l} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '0.68rem', color: C.muted }}>
                  <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: c, display: 'inline-block', flexShrink: 0 }} />{l}
                </span>
              ))}
            </div>
          </div>
        </div>
        <div style={{ height: '10px', background: C.surfaceAlt, borderRadius: '5px', overflow: 'hidden', display: 'flex', gap: '1px' }}>
          <div style={{ width: `${pctOf(cumpleCount)}%`, background: C.olive, transition: 'width 0.5s' }} />
          <div style={{ width: `${pctOf(noAplicaCount)}%`, background: C.slate, transition: 'width 0.5s' }} />
          <div style={{ width: `${pctOf(noCumpleCount)}%`, background: C.coral, transition: 'width 0.5s' }} />
          <div style={{ flex: 1, background: C.disabled, opacity: 0.3 }} />
        </div>
        {classification.capped && classification.blockers && classification.blockers.length > 0 && (
          <div style={{ marginTop: '12px', padding: '10px 12px', background: C.coral + '12', border: `1px solid ${C.coral}40`, borderRadius: '4px' }}>
            <div style={{ fontSize: '0.72rem', fontWeight: '600', color: C.coral, marginBottom: '4px' }}>
              Clasificación capada a {classification.label} por prerrequisitos
            </div>
            <div style={{ fontSize: '0.68rem', color: C.muted, lineHeight: 1.5 }}>
              El % global daría <strong>{classification.byPctLabel}</strong>, pero el manual BREEAM ES exige puntos mínimos en estos criterios:
              <div style={{ marginTop: '4px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {classification.blockers.map(b => (
                  <span key={b.code} style={{ ...MONO, fontSize: '0.66rem' }}>
                    · <strong>{b.code}</strong>: {b.pts} pts obtenidos / <strong>{b.required} pts</strong> requeridos para subir de {classification.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* KPI cards — 4 estados del criterio */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '1rem' }}>
        {[
          { title: 'Cumple', value: cumpleCount, sub: `${Math.round(pctOf(cumpleCount))}% del total · suma puntos`, dot: C.olive, icon: CheckCircle },
          { title: 'No aplica', value: noAplicaCount, sub: `Excluidos del cómputo`, dot: C.slate, icon: MinusCircle },
          { title: 'No cumple', value: noCumpleCount, sub: 'Evaluados sin puntos obtenidos', borderColor: noCumpleCount > 0 ? C.coral : undefined, dot: C.coral, icon: Warning },
          { title: 'Pendiente', value: pendienteCount, sub: 'Sin evaluar', dot: C.disabled, icon: Clock },
        ].map(({ title, value, sub, badge, badgeColor, dot, borderColor, icon: Icon }) => (
          <div key={title} style={{ ...CARD, borderLeft: borderColor ? `3px solid ${borderColor}` : undefined }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: '0.67rem', fontWeight: '600', color: C.muted, textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: '8px' }}>{title}</div>
                <div style={{ fontSize: '1.875rem', fontWeight: '700', color: C.text, lineHeight: 1, ...MONO }}>{value}</div>
                <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  {dot && <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: dot, flexShrink: 0 }} />}
                  <span style={{ fontSize: '0.72rem', color: C.muted }}>{sub}</span>
                  {badge && <span style={{ background: badgeColor + '18', color: badgeColor, padding: '1px 7px', borderRadius: '3px', fontSize: '0.65rem', fontWeight: '600' }}>{badge}</span>}
                </div>
              </div>
              <Icon size={22} style={{ color: C.muted, flexShrink: 0, marginTop: '2px' }} />
            </div>
          </div>
        ))}
      </div>

      {/* Radar + Tabla ponderaciones */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.6fr', gap: '1.25rem' }}>

        {/* Radar */}
        <div style={{ ...CARD, display: 'flex', flexDirection: 'column' }}>
          <h3 style={{ margin: '0 0 1rem', fontSize: '0.875rem', fontWeight: '600', color: C.text }}>Cobertura por Categoría</h3>
          <div style={{ flex: 1, minHeight: 260 }}>
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} margin={{ top: 8, right: 20, bottom: 8, left: 20 }}>
                <PolarGrid stroke={C.border} />
                <PolarAngleAxis dataKey="subject" tick={{ fill: C.muted, fontSize: 10, fontFamily: "'DM Sans', sans-serif" }} />
                <Radar name="Objetivo" dataKey="objetivo" stroke={C.amber} fill="none" strokeWidth={1.5} strokeDasharray="4 3" />
                <Radar name="Actual" dataKey="actual" stroke={C.olive} fill={C.olive} fillOpacity={0.18} strokeWidth={2} />
                <Tooltip contentStyle={{ background: 'white', border: `1px solid ${C.border}`, borderRadius: '4px', fontFamily: "'DM Sans', sans-serif", fontSize: '0.78rem' }}
                  formatter={(v, n, p) => [`${v}%`, p.payload.fullName]} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
          <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', marginTop: '4px' }}>
            {[['Actual', C.olive, false], ['Objetivo 65%', C.amber, true]].map(([label, color, dashed]) => (
              <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '0.7rem', color: C.muted }}>
                <div style={{ width: '18px', height: '2px', background: color, ...(dashed ? { borderTop: `2px dashed ${color}`, height: 0, background: 'none' } : {}) }} />
                {label}
              </div>
            ))}
          </div>
        </div>

        {/* Tabla ponderaciones */}
        <div style={{ ...CARD, padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}` }}>
            <h3 style={{ margin: 0, fontSize: '0.875rem', fontWeight: '600', color: C.text }}>Puntuación Ponderada BREEAM NC</h3>
            <p style={{ margin: '3px 0 0', fontSize: '0.72rem', color: C.muted }}>
              Puntuación ponderada: <span style={{ ...MONO, fontWeight: '700', color: C.text }}>{weightedTotal.toFixed(1)}%</span>
              <span style={{ marginLeft: '8px', background: classification.color + '18', color: classification.color, padding: '1px 8px', borderRadius: '3px', fontWeight: '600', fontSize: '0.67rem' }}>{classification.label}</span>
            </p>
          </div>
          <div>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.775rem', ...FONT }}>
              <thead style={{ position: 'sticky', top: 0 }}>
                <tr style={{ background: C.surfaceAlt }}>
                  {['Categoría', 'Peso', 'Disp.', 'Obten.', 'Pond.', 'Estado'].map(h => (
                    <th key={h} style={{ padding: '8px 10px', textAlign: h === 'Categoría' ? 'left' : 'center', fontWeight: '600', color: C.muted, fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', borderBottom: `1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {categoryStats.map((c, i) => {
                  const CatIcon = CAT_ICONS[c.cat] || ClipboardText
                  const stateColor = c.pct >= 65 ? C.olive : c.pct >= 35 ? C.amber : c.pct > 0 ? C.coral : C.disabled
                  const stateLabel = c.pct >= 65 ? 'En objetivo' : c.pct >= 35 ? 'Parcial' : c.pct > 0 ? 'En riesgo' : 'Pendiente'
                  const isExp = expandedCat === c.cat
                  return [
                    <tr key={c.cat}
                      onClick={() => setExpandedCat(isExp ? null : c.cat)}
                      style={{ background: i % 2 === 0 ? 'white' : C.surface, borderBottom: `1px solid ${C.border}`, cursor: 'pointer' }}
                      onMouseOver={e => e.currentTarget.style.background = C.surfaceAlt}
                      onMouseOut={e => e.currentTarget.style.background = i % 2 === 0 ? 'white' : C.surface}>
                      <td style={{ padding: '8px 10px', color: C.text, fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <CatIcon size={13} style={{ color: C.muted, flexShrink: 0 }} />
                        <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '130px' }}>{c.cat}</span>
                      </td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: C.muted, ...MONO }}>{c.weight == null ? '+1%/pt' : `${(c.weight * 100).toFixed(1)}%`}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: C.muted, ...MONO }}>{c.maxPts}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: C.text, fontWeight: '600', ...MONO }}>{c.points}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center', color: C.text, ...MONO }}>{c.weightedScore.toFixed(1)}</td>
                      <td style={{ padding: '8px 10px', textAlign: 'center' }}>
                        <span style={{ background: stateColor + '18', color: stateColor, padding: '1px 7px', borderRadius: '3px', fontSize: '0.63rem', fontWeight: '600' }}>{stateLabel}</span>
                      </td>
                    </tr>,
                    isExp && (
                      <tr key={`${c.cat}-exp`} style={{ background: C.surface }}>
                        <td colSpan={6} style={{ padding: '4px 0 8px', borderBottom: `1px solid ${C.border}` }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.72rem', fontFamily: "'DM Sans', sans-serif" }}>
                            <thead>
                              <tr style={{ background: C.surfaceAlt }}>
                                {['Criterio', 'Nombre', 'Obtenidos', 'Disponibles', 'Estado'].map(h => (
                                  <th key={h} style={{ padding: '5px 12px', textAlign: h === 'Criterio' || h === 'Nombre' ? 'left' : 'center', fontWeight: '600', color: C.muted, fontSize: '0.6rem', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {Object.entries(c.data.criteria).map(([code, cr], idx) => {
                                const ev = Object.values(evals).find(e => e.category === c.cat && e.criteria_code === code)
                                const obtained = ev ? ((ev.puntos_auditoria ?? ev.points_obtained) || 0) : null
                                let parsedJ = null
                                try { parsedJ = ev?.justificacion ? JSON.parse(ev.justificacion) : null } catch {}
                                const noAplica = parsedJ?.no_aplica === true
                                const stColor = !ev ? C.disabled : noAplica ? C.slate : obtained > 0 ? C.olive : C.coral
                                const stLabel = !ev ? 'Pendiente' : noAplica ? 'No aplica' : obtained > 0 ? 'Cumple' : 'No cumple'
                                const isCurrentPhase = CRITERIA_PHASES[code] === currentPhase
                                return (
                                  <tr key={code} style={{ background: idx % 2 === 0 ? 'white' : C.surface, borderTop: `1px solid ${C.border}`, borderLeft: isCurrentPhase ? `3px solid ${PHASE_COLORS[currentPhase] || C.slate}` : '3px solid transparent' }}>
                                    <td style={{ padding: '5px 12px', whiteSpace: 'nowrap', fontFamily: "'IBM Plex Mono', monospace", fontSize: '0.68rem' }}>
                                      <span style={{ fontWeight: '700', color: C.text }}>{code}</span>
                                      {isCurrentPhase && <span style={{ marginLeft: '4px', fontSize: '0.55rem', fontWeight: '700', color: PHASE_COLORS[currentPhase] || C.slate, background: (PHASE_COLORS[currentPhase] || C.slate) + '18', padding: '0 3px', borderRadius: '2px' }}>F{currentPhase}</span>}
                                    </td>
                                    <td style={{ padding: '5px 12px', color: C.muted, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cr.name}</td>
                                    <td style={{ padding: '5px 12px', textAlign: 'center', fontWeight: '700', color: obtained > 0 ? C.olive : obtained === 0 ? C.coral : C.disabled, fontFamily: "'IBM Plex Mono', monospace" }}>
                                      {obtained !== null ? obtained : '—'}
                                    </td>
                                    <td style={{ padding: '5px 12px', textAlign: 'center', color: C.muted, fontFamily: "'IBM Plex Mono', monospace" }}>{cr.points}</td>
                                    <td style={{ padding: '5px 12px', textAlign: 'center' }}>
                                      <span style={{ background: stColor + '18', color: stColor, padding: '1px 7px', borderRadius: '3px', fontSize: '0.6rem', fontWeight: '600', whiteSpace: 'nowrap' }}>{stLabel}</span>
                                    </td>
                                  </tr>
                                )
                              })}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    )
                  ]
                })}
              </tbody>
            </table>
          </div>
          <div style={{ padding: '7px 20px', borderTop: `1px solid ${C.border}`, background: C.surface, fontSize: '0.67rem', color: C.muted }}>
            Ponderaciones BREEAM NC 2018 (ES) · Clic en fila para expandir créditos
          </div>
        </div>
      </div>

      {/* Timeline */}
      <div style={CARD}>
        <h3 style={{ margin: '0 0 1.5rem', fontSize: '0.875rem', fontWeight: '600', color: C.text }}>Fase del Expediente</h3>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ position: 'absolute', top: '19px', left: '10%', right: '10%', height: '2px', background: C.border, zIndex: 0 }} />
          {PHASES.map((phase) => {
            const isPast = phase.id < currentPhase
            const isActive = phase.id === currentPhase
            const isFuture = phase.id > currentPhase
            return (
              <div key={phase.id} onClick={() => onPhaseChange?.(phase.id)}
                style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '10px', zIndex: 1, cursor: 'pointer', flex: 1 }}>
                <div style={{
                  width: '38px', height: '38px', borderRadius: '50%',
                  background: isActive || isPast ? phase.color : 'white',
                  border: `2px solid ${isFuture ? C.border : phase.color}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  opacity: isPast ? 0.45 : 1,
                  transition: 'all 0.2s',
                  boxShadow: isActive ? `0 0 0 5px ${phase.color}22` : 'none',
                }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: '700', color: isActive || isPast ? 'white' : C.muted, ...MONO }}>{phase.id}</span>
                </div>
                <div style={{ textAlign: 'center', maxWidth: '90px' }}>
                  <div style={{ fontSize: '0.7rem', fontWeight: isActive ? '600' : '400', color: isFuture ? C.muted : C.text, lineHeight: 1.3 }}>{phase.label}</div>
                  {isActive && <div style={{ fontSize: '0.6rem', color: phase.color, fontWeight: '600', marginTop: '3px' }}>● Fase actual</div>}
                </div>
              </div>
            )
          })}
        </div>
        <div style={{ marginTop: '1.25rem', padding: '10px 14px', background: C.surface, borderRadius: '6px', border: `1px solid ${C.border}`, fontSize: '0.775rem', color: C.muted }}>
          {(() => {
            const phaseCodes = Object.entries(CRITERIA_PHASES).filter(([, p]) => p === currentPhase).map(([code]) => code)
            let phCumple = 0, phNoAplica = 0, phNoCumple = 0
            phaseCodes.forEach(code => {
              const ev = Object.values(project.evaluations || {}).find(e => e.criteria_code === code)
              if (!ev) return
              let j = null; try { j = ev.justificacion ? JSON.parse(ev.justificacion) : null } catch {}
              if (j?.no_aplica) phNoAplica++
              else if (((ev.puntos_auditoria ?? ev.points_obtained) || 0) > 0) phCumple++
              else phNoCumple++
            })
            const phPendiente = Math.max(0, phaseCodes.length - phCumple - phNoAplica - phNoCumple)
            const allResolved = phaseCodes.length > 0 && (phCumple + phNoAplica) === phaseCodes.length
            return (
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <span>Criterios de esta fase: <strong style={{ color: C.text }}>{phaseCodes.length}</strong></span>
                {[
                  ['Cumple', phCumple, C.olive],
                  ['No aplica', phNoAplica, C.slate],
                  ['No cumple', phNoCumple, C.coral],
                  ['Pendiente', phPendiente, C.disabled],
                ].map(([label, val, color]) => (
                  <span key={label} style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: color, display: 'inline-block' }} />
                    {label}: <strong style={{ color, marginLeft: '3px' }}>{val}</strong>
                  </span>
                ))}
                {allResolved && (
                  <span style={{ background: C.olive + '18', color: C.olive, padding: '1px 8px', borderRadius: '3px', fontSize: '0.7rem', fontWeight: '600' }}>✓ Fase completada</span>
                )}
              </div>
            )
          })()}
        </div>
      </div>

    </div>
  )
}
